export class ProductFinishesPicker extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {MutationObserver | undefined} */
  #drawerObserver;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#refreshDrawer();
    this.#observeDrawer();

    const section = this.closest('.shopify-section');
    section?.addEventListener('variant:update', this.#handleVariantUpdate, { signal });

    queueMicrotask(() => this.refreshInitialFinish());
  }

  disconnectedCallback() {
    this.#drawerObserver?.disconnect();
    this.#controller?.abort();
  }

  /** Re-sync trigger preview after DOM updates (e.g. quick-add modal morph). */
  refreshInitialFinish() {
    this.#refreshDrawer();
    this.#selectInitialFinish();
  }

  #observeDrawer() {
    const drawerBody = this.querySelector('.product-finishes-picker__drawer-body');
    if (!(drawerBody instanceof HTMLElement)) return;

    this.#drawerObserver?.disconnect();
    this.#drawerObserver = new MutationObserver(() => {
      this.#refreshDrawer();
    });
    this.#drawerObserver.observe(drawerBody, { childList: true, subtree: true });
  }

  #refreshDrawer() {
    this.#revealGroups();
    this.#bindClicks();
  }

  #revealGroups() {
    this.querySelectorAll('[data-finish-group]').forEach((group) => {
      const hasItems = group.querySelector('[data-finish-item]');
      group.hidden = !hasItems;
    });
  }

  #bindClicks() {
    this.querySelectorAll('[data-finish-item]').forEach((button) => {
      button.removeEventListener('click', this.#handleFinishClick);
      button.addEventListener('click', this.#handleFinishClick);
    });
  }

  #handleVariantUpdate = () => {
    requestAnimationFrame(() => {
      this.#refreshDrawer();
      this.#syncFromVariantSelect();
    });
  };

  #getVariantPicker() {
    return (
      this.closest('variant-picker') ||
      document.querySelector('variant-picker[data-template-product-match="true"]')
    );
  }

  #getHiddenFinishSelect() {
    const picker = this.#getVariantPicker();
    if (!(picker instanceof HTMLElement)) return null;

    const optionName = this.dataset.standardVariantOptionName || 'Finishes';
    const select = picker.querySelector(
      `select.variant-option__select[data-finishes-dialog-option="${CSS.escape(optionName)}"]`
    );

    return select instanceof HTMLSelectElement ? select : null;
  }

  /** @param {string} source @param {string} finishName */
  #findFinishButton(source, finishName) {
    return Array.from(this.querySelectorAll('[data-finish-item]')).find((item) => {
      if (!(item instanceof HTMLButtonElement)) return false;
      return item.dataset.finishSource === source && item.dataset.finishName === finishName;
    });
  }

  /** @param {string} finishName */
  #findFinishButtonForVariant(finishName) {
    if (!finishName) return null;

    const customButton = this.#findFinishButton('custom', finishName);
    if (customButton instanceof HTMLButtonElement) return customButton;

    const standardButton = this.#findFinishButton('standard', finishName);
    if (standardButton instanceof HTMLButtonElement) return standardButton;

    return null;
  }

  #syncFromVariantSelect() {
    const select = this.#getHiddenFinishSelect();
    if (!select?.value) return;

    const button = this.#findFinishButtonForVariant(select.value);
    if (button instanceof HTMLButtonElement) {
      this.#setSelected(button, { syncVariant: false });
    }
  }

  #selectInitialFinish() {
    const hasSelection = this.querySelector('[data-finish-item][data-current-checked="true"]');
    if (hasSelection) {
      if (hasSelection instanceof HTMLButtonElement) {
        this.#updateTrigger(hasSelection);
        this.#dispatchFinishSelected(hasSelection.dataset.finishName || '');
      }
      return;
    }

    const select = this.#getHiddenFinishSelect();
    if (select?.value) {
      const matchedButton = this.#findFinishButtonForVariant(select.value);
      if (matchedButton instanceof HTMLButtonElement) {
        this.#setSelected(matchedButton, { syncVariant: false });
        return;
      }
    }

    const firstItem = this.querySelector('[data-finish-item]');
    if (firstItem instanceof HTMLButtonElement) {
      this.#setSelected(firstItem);
    }
  }

  /** @param {Event} event */
  #handleFinishClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    if (button.dataset.optionAvailable === 'false') return;

    this.#setSelected(button);
  };

  /**
   * @param {HTMLButtonElement} button
   * @param {{ syncVariant?: boolean }} [options]
   */
  #setSelected(button, options = {}) {
    const { syncVariant = true } = options;

    this.querySelectorAll('[data-finish-item]').forEach((item) => {
      if (!(item instanceof HTMLButtonElement)) return;

      const isSelected = item === button;
      item.dataset.currentChecked = isSelected ? 'true' : 'false';
      item.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.#updateTrigger(button);

    const finishName = button.dataset.finishName || '';
    if (finishName) {
      this.#dispatchFinishSelected(finishName);
    }

    if (syncVariant && finishName) {
      this.#applyFinishVariant(finishName);
    }
  }

  /** @param {HTMLButtonElement} button */
  #updateTrigger(button) {
    const finishName = button.dataset.finishName || '';
    const imageUrl = button.dataset.finishImageUrl || '';

    const selectedLabel = this.querySelector('[data-finish-selected-label]');
    if (selectedLabel instanceof HTMLElement) {
      selectedLabel.textContent = finishName;
      selectedLabel.hidden = finishName === '';
    }

    const selectedPreview = this.querySelector('[data-finish-selected-preview]');
    const selectedImage = this.querySelector('[data-finish-selected-image]');
    if (selectedPreview instanceof HTMLElement && selectedImage instanceof HTMLElement) {
      if (imageUrl) {
        selectedImage.style.setProperty('--finish-image', `url(${imageUrl})`);
        selectedPreview.hidden = false;
      } else {
        selectedImage.style.removeProperty('--finish-image');
        selectedPreview.hidden = true;
      }
    }
  }

  /** @param {string} finishName */
  #dispatchFinishSelected(finishName) {
    document.documentElement.dataset.activeFinishFilter = finishName;

    this.dispatchEvent(
      new CustomEvent('finish:selected', {
        bubbles: true,
        detail: { finishName },
      })
    );
  }

  /** @param {string} finishName */
  #applyFinishVariant(finishName) {
    const select = this.#getHiddenFinishSelect();
    if (!select || !finishName) return;

    const matchingOption = Array.from(select.options).find((option) => option.value === finishName);
    if (!matchingOption) return;

    if (select.value === finishName) return;

    select.value = finishName;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/**
 * @param {ParentNode} [root]
 */
export function refreshProductFinishesPickers(root = document) {
  root.querySelectorAll('product-finishes-picker').forEach((picker) => {
    if (picker instanceof ProductFinishesPicker) {
      picker.refreshInitialFinish();
    }
  });
}

if (!customElements.get('product-finishes-picker')) {
  customElements.define('product-finishes-picker', ProductFinishesPicker);
}
